import { useQueryClient } from '@tanstack/react-query';
import { useEffect, useRef } from 'react';
import { CONVERSATIONS_QUERY_KEY, useAdminConversations } from './useAdminConversations';

/** The server writes the generated title shortly after the first message; check a few times. */
const TITLE_POLL_INTERVAL_MS = 3000;
const TITLE_POLL_MAX_ATTEMPTS = 5;

/**
 * Title of the open conversation. The title is generated in the background after the first
 * message, so while the conversation has messages but no title yet the list is refetched a few
 * times (and once more when a reply finishes) until it shows up. This avoids a second,
 * token-in-URL realtime socket just for titles.
 */
export function useConversationTitle(
  conversationPublicId: string,
  state: { hasMessages: boolean; isStreaming: boolean }
): string | null {
  const queryClient = useQueryClient();
  const attemptsRef = useRef(0);
  const { data } = useAdminConversations(true);
  const title =
    data?.find((item) => item.conversationPublicId === conversationPublicId)?.title ?? null;
  const waitingForTitle = state.hasMessages && !title;

  useEffect(() => {
    attemptsRef.current = 0;
  }, [conversationPublicId]);

  useEffect(() => {
    if (!state.isStreaming)
      void queryClient.invalidateQueries({ queryKey: CONVERSATIONS_QUERY_KEY });
  }, [state.isStreaming, queryClient]);

  useEffect(() => {
    if (!waitingForTitle) return;
    const timer = setInterval(() => {
      if (attemptsRef.current >= TITLE_POLL_MAX_ATTEMPTS) return clearInterval(timer);
      attemptsRef.current += 1;
      void queryClient.invalidateQueries({ queryKey: CONVERSATIONS_QUERY_KEY });
    }, TITLE_POLL_INTERVAL_MS);
    return () => clearInterval(timer);
  }, [waitingForTitle, conversationPublicId, queryClient]);

  return title;
}
